import { CampaignResponse, Conditions, Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import {
    ApplyContext,
    ConflictItem,
    FetchContext,
    FetchParams,
    OrderContext,
    SubmitError,
    ValidationError,
} from '../types';
import { CampaignStrategy } from './campaign-strategy';
import { computeProporcionalMultiplier } from './bonificacao-proporcional.strategy';

const CONDITION_FIELD = 'QUANTIDADE_ITEM';
const PAYMENT_CONDITION_BONIFICACAO = '100';

const getConditionItems = (campaign: CampaignResponse): Conditions[] =>
    campaign.condicoes.filter((c) => c.campo === CONDITION_FIELD);

export class DescontoStrategy implements CampaignStrategy {
    readonly tipo = 'DESCONTO' as const;

    isAvailableForOrder(
        _campaign: CampaignResponse,
        _context: OrderContext,
    ): boolean {
        return true;
    }

    validateEligibility(
        campaign: CampaignResponse,
        availableItems: ItemResponse[],
    ): ValidationError[] {
        const errors: ValidationError[] = [];
        for (const cond of getConditionItems(campaign)) {
            const item = availableItems.find(
                (i) => i.number === cond.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'CONDITION_ITEM_MISSING',
                    message: `SKU ${cond.numeroItem} (condição) não disponível`,
                    numeroItem: cond.numeroItem,
                });
            }
        }
        for (const result of campaign.resultados) {
            if (!result.numeroItem) continue;
            const item = availableItems.find(
                (i) => i.number === result.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'RESULT_ITEM_MISSING',
                    message: `SKU ${result.numeroItem} (resultado) não disponível`,
                    numeroItem: result.numeroItem,
                });
            }
        }
        return errors;
    }

    detectConflicts(
        campaign: CampaignResponse,
        otherSelected: CampaignResponse[],
    ): ConflictItem[] {
        const conflicts: ConflictItem[] = [];
        const ownNumbers = new Set<string>([
            ...getConditionItems(campaign).map((c) => c.numeroItem),
            ...campaign.resultados.map((r) => r.numeroItem).filter(Boolean),
        ]);

        for (const other of otherSelected) {
            if (other.id === campaign.id) continue;
            const otherNumbers = [
                ...other.condicoes
                    .filter((c) => c.campo === 'QUANTIDADE_ITEM' || c.campo === 'ATINGIR_COTA')
                    .map((c) => c.numeroItem),
                ...other.resultados.map((r) => r.numeroItem).filter(Boolean),
            ];

            for (const n of otherNumbers) {
                if (ownNumbers.has(n)) {
                    conflicts.push({
                        numeroItem: n,
                        campaignIds: [campaign.id, other.id],
                    });
                }
            }
        }

        return conflicts;
    }

    onEnable(
        _campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        return orderItems;
    }

    applyResultToItem(
        item: ItemResponse,
        result: Result,
        campaign: CampaignResponse,
        _context: ApplyContext,
    ): ItemResponse {
        const baseQtd = Number(result.qtd) || 0;
        return {
            ...item,
            campaign,
            result,
            disabled: true,
            quantity: baseQtd,
            discount: Number(result.desconto),
        };
    }

    recalculateOnQuantityChange(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        // Escala proporcional do R-side conforme C-side (replica o algoritmo
        // antigo de updateItemApi: tempI.quantity = result.qtd * floor(qty/valor)).
        // O desconto (result.desconto) permanece fixo e é setado em applyResultToItem.
        const { found, multiplier } = computeProporcionalMultiplier(campaign, orderItems);
        if (!found) return orderItems;

        const resultByNumero = new Map<string, Result>();
        for (const r of campaign.resultados) {
            if (!r.numeroItem) continue;
            resultByNumero.set(r.numeroItem.toLowerCase(), r);
        }

        return orderItems.map((item) => {
            const result = resultByNumero.get((item.number ?? '').toLowerCase());
            if (!result) return item;
            if (item.campaign && item.campaign.id !== campaign.id) return item;

            const baseQtd = Number(result.qtd) || 0;
            const newQty = baseQtd * multiplier;
            if (item.quantity === newQty) return item;
            return { ...item, quantity: newQty };
        });
    }

    validateForSubmit(): SubmitError[] {
        return [];
    }

    requiresExtraFetchParams(
        _campaign: CampaignResponse,
        _context: FetchContext,
    ): Partial<FetchParams> {
        return {};
    }

    acceptsPaymentCondition(paymentConditionId: string): boolean {
        return paymentConditionId !== PAYMENT_CONDITION_BONIFICACAO;
    }
}
